// src/store/slices/projectSlice.ts

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { AppDispatch } from '..'
import { clearEditingPrediction } from './predictionSlice'
import { Tag } from '../../types/models/tag'
import { RootState } from '../index'
import { FileMetadata } from '../../types/file'
import { Model } from '../../types/models/model'
import { ProjectAnnotationType } from '../../types/models/project'
export type ActiveTab = 'annotated' | 'content'

export type DatasetSubView = 'Overview' | 'Labeling' | 'Tags' | 'Content'

export const DatasetSubNavItems = ['Overview', 'Tags', 'Content', 'Labeling'] as DatasetSubView[]

export type InferenceSubView =
  | 'Overview'
  | 'Adaptive Active Labeling'
  | 'Real Time'
  | 'Map (Beta)'
  | 'Synthetic Data'
export const InferenceSubNavItems = [
  'Overview',
  'Adaptive Active Labeling',
  'Real Time',
  'Map (Beta)',
  // 'Synthetic Data',
] as InferenceSubView[]

export type ModelSubView =
  | 'Overview'
  | 'Training'
  | 'Performance'
  | 'Deployment'
  | 'Compare'
  | 'Model Datasets'
  | 'Versions'
export const ModelSubNavItems = [
  'Overview',
  'Training',
  'Performance',
  'Model Datasets',
  'Deployment',
  'Compare',
  'Versions',
] as ModelSubView[]

export type ProjectType = ProjectAnnotationType

export type NewProjectData = {
  name: string | undefined
  description: string | undefined
  type: ProjectType
  labelsFile: File | undefined
  labelsFileInfo: FileMetadata | undefined
}

export type DashboardSubView = 'Overview' | 'Users' | 'Orgs'
export const DashboardSubNavItems = ['Overview'] as DashboardSubView[]

export interface ProjectState {
  activeTab: ActiveTab
  selectedModelTab: string | undefined
  activeDatasetSubView: DatasetSubView
  activeInferenceSubView: InferenceSubView
  activeModelSubView: ModelSubView
  filterTags: Tag[]
  selectedProjectId: string | undefined
  selectedAnnotationIds: string[]
  aiResponses: string[]
  newProjectData: NewProjectData | null
  deployedModels: Model[]
  activeDashboardSubView: DashboardSubView
  showingNewAAL: boolean
}

const initialState: ProjectState = {
  activeTab: (localStorage.getItem('activeTab') as ActiveTab) || 'content',
  selectedModelTab: localStorage.getItem('selectedModelTab') || undefined,
  activeDatasetSubView:
    (localStorage.getItem('activeDatasetSubView') as DatasetSubView) || 'Overview',
  activeInferenceSubView: 'Adaptive Active Labeling',
  activeModelSubView: (localStorage.getItem('activeModelSubView') as ModelSubView) || 'Overview',
  filterTags: [],
  selectedProjectId: localStorage.getItem('selectedProjectId') || undefined,
  selectedAnnotationIds: [],
  aiResponses: [],
  newProjectData: null,
  deployedModels: [],
  activeDashboardSubView: 'Overview',
  showingNewAAL: false,
}

const projectSlice = createSlice({
  name: 'project',
  initialState,
  reducers: {
    setNewProjectData: (state, action: PayloadAction<NewProjectData>) => {
      state.newProjectData = action.payload
    },

    addSelectedAnnotationId: (state, action: PayloadAction<string>) => {
      const newSet = Array.from(new Set([...state.selectedAnnotationIds, action.payload]))
      state.selectedAnnotationIds = newSet || []
    },

    removeSelectedAnnotationId: (state, action: PayloadAction<string>) => {
      state.selectedAnnotationIds = state.selectedAnnotationIds.filter(
        (id: string) => id !== action.payload
      )
    },

    toggleSelectedAnnotationId: (state, action: PayloadAction<string>) => {
      if (state.selectedAnnotationIds.includes(action.payload)) {
        state.selectedAnnotationIds = state.selectedAnnotationIds.filter(
          (id: string) => id !== action.payload
        )
      } else {
        const newSet = Array.from(new Set([...state.selectedAnnotationIds, action.payload]))
        state.selectedAnnotationIds = newSet || []
      }
    },

    setActiveTab: (state, action: PayloadAction<ActiveTab>) => {
      state.activeTab = action.payload
      localStorage.setItem('activeTab', action.payload)
    },

    setSelectedModelTab: (state, action: PayloadAction<string | undefined>) => {
      state.selectedModelTab = action.payload
      if (action.payload) {
        localStorage.setItem('selectedModelTab', action.payload)
      } else {
        localStorage.removeItem('selectedModelTab')
      }
    },

    clearSelectedModelTab: (state) => {
      state.selectedModelTab = undefined
      localStorage.removeItem('selectedModelTab')
    },

    setActiveDatasetSubView: (state, action: PayloadAction<DatasetSubView>) => {
      state.activeDatasetSubView = action.payload
      localStorage.setItem('activeDatasetSubView', action.payload)
    },

    setActiveInferenceSubView: (state, action: PayloadAction<InferenceSubView>) => {
      state.activeInferenceSubView = action.payload
      localStorage.setItem('activeInferenceSubView', action.payload)
    },

    setActiveModelSubView: (state, action: PayloadAction<ModelSubView>) => {
      state.activeModelSubView = action.payload
      localStorage.setItem('activeModelSubView', action.payload.toLowerCase())
    },

    setFilterTags: (state, action: PayloadAction<Tag[]>) => {
      state.filterTags = action.payload
    },

    clearFilterTags: (state) => {
      state.filterTags = []
    },

    addFilterTag: (state, action: PayloadAction<Tag>) => {
      const filterExists = state.filterTags.find((t: Tag) => t.id === action.payload.id)
      if (!filterExists) {
        state.filterTags = [...state.filterTags, action.payload]
      }
    },

    removeFilterTag: (state, action: PayloadAction<Tag>) => {
      state.filterTags = state.filterTags.filter((t: Tag) => t.id !== action.payload.id)
    },

    toggleFilterTag: (state, action: PayloadAction<Tag>) => {
      const filterExists = state.filterTags.find((t: Tag) => t.id === action.payload.id)
      if (!filterExists) {
        state.filterTags = [...state.filterTags, action.payload]
      } else {
        state.filterTags = state.filterTags.filter((t: Tag) => t.id !== action.payload.id)
      }
    },

    setSelectedProjectId: (state, action: PayloadAction<string>) => {
      state.selectedProjectId = action.payload
      localStorage.setItem('selectedProjectId', action.payload)
    },

    clearSelectedProjectId: (state) => {
      state.selectedProjectId = undefined
      localStorage.removeItem('selectedProjectId')
      // Also clear navigation state when project is cleared
      localStorage.removeItem('activeTab')
      localStorage.removeItem('selectedModelTab')
      localStorage.removeItem('activeDatasetSubView')
      localStorage.removeItem('activeInferenceSubView')
      // Clear dataset selections when project is cleared
      localStorage.removeItem('datasetSources')
      localStorage.removeItem('trainingSource')
      localStorage.removeItem('validationSource')
      state.activeTab = 'content'
      state.selectedModelTab = undefined
      state.activeDatasetSubView = 'Overview'
      state.activeInferenceSubView = 'Adaptive Active Labeling'
    },

    addAIResponse: (state, action: PayloadAction<string>) => {
      state.aiResponses.push(action.payload)
    },

    setAIResponse: (state, action: PayloadAction<string[]>) => {
      state.aiResponses = action.payload
    },

    clearAIResponses: (state) => {
      state.aiResponses = []
    },

    setDeployedModels: (state, action: PayloadAction<Model[]>) => {
      state.deployedModels = action.payload
    },

    setActiveDashboardSubView: (state, action: PayloadAction<DashboardSubView>) => {
      state.activeDashboardSubView = action.payload
    },

    setShowingNewAAL: (state, action: PayloadAction<boolean>) => {
      state.showingNewAAL = action.payload
    },
  },
})

export const {
  addSelectedAnnotationId,
  removeSelectedAnnotationId,
  toggleSelectedAnnotationId,
  setActiveTab,
  setSelectedModelTab,
  clearSelectedModelTab,
  setActiveDatasetSubView,
  setActiveInferenceSubView,
  setActiveModelSubView,
  setFilterTags,
  addFilterTag,
  removeFilterTag,
  clearFilterTags,
  toggleFilterTag,
  setSelectedProjectId,
  clearSelectedProjectId,
  setAIResponse,
  addAIResponse,
  clearAIResponses,
  setDeployedModels,
  setNewProjectData,
  setActiveDashboardSubView,
  setShowingNewAAL,
} = projectSlice.actions

export default projectSlice.reducer

// Selector
export const selectSelectedAnnotationIds = (state: RootState) => state.project.selectedAnnotationIds
export const selectActiveTab = (state: { project: ProjectState }) => state.project.activeTab
export const selectSelectedModelTab = (state: { project: ProjectState }) =>
  state.project.selectedModelTab
export const selectActiveDatasetSubView = (state: RootState) => state.project.activeDatasetSubView
export const selectActiveInferenceSubView = (state: RootState) =>
  state.project.activeInferenceSubView
export const selectActiveModelSubView = (state: RootState) => state.project.activeModelSubView
export const selectFilterTags = (state: { project: ProjectState }) => state.project.filterTags
export const selectSelectedProjectId = (state: RootState) => state.project.selectedProjectId
export const selectAiResponses = (state: RootState) => state.project.aiResponses
export const selectNewProjectData = (state: RootState) => state.project.newProjectData
export const selectDeployedModels = (state: RootState) => state.project.deployedModels
export const selectDeployedModelsCount = (state: RootState) => state.project.deployedModels.length
export const selectActiveDashboardSubView = (state: RootState) =>
  state.project.activeDashboardSubView
export const selectShowingNewAAL = (state: RootState) => state.project.showingNewAAL

// When switching projects from the UI, clear any in-progress prediction review
export const setSelectedProjectIdAndClearPrediction =
  (projectId: string) => (dispatch: AppDispatch) => {
    dispatch(setSelectedProjectId(projectId))
    dispatch(clearEditingPrediction())
  }
